import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Divider,
  TextField,
  Stack,
  Typography,
  Radio,
} from '@mui/material';
import type { Account } from '@repo/api-gw-sdk';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { useCreateAccount } from './useCreateAccount';
import { useUpdateAccount } from './useUpdateAccount';

import CopyableCode from '../shared/copyableCode';

enum AWSDeploymentMethod {
  CloudFormation = 'CloudFormation',
  Terraform = 'Terraform',
}

export const CreateAWSAccountPanel = ({
  accountType,
  onCreated,
  setLoading,
  initialAccount,
}: {
  accountType: CloudAccountType;
  onCreated: (sourceAccountId: string) => void;
  setLoading: (loading: boolean) => void;
  initialAccount?: Account;
}) => {
  const {
    accountId: awsAccountId,
    setAccountId,
    roleName: accountRoleName,
    setRoleName: setAccountRoleName,
    orgRoleName,
    setOrgRoleName,
    organizationOUID,
    setOrganizationOUID,
    validationInProgress,
    isCreated,
    isCreationInProgress,
    hasError,
    setValidationInProgress,
    setHasError,
    retry,
    createAccount,
    validateAccount,
    validateOrganization,
    connectOrganization,
  } = useCreateAccount(
    accountType,
    onCreated,
    initialAccount?.providerAccountId
  );
  const { updateAccount } = useUpdateAccount(
    onCreated,
    setValidationInProgress,
    setHasError,
    initialAccount,
    false
  );
  const [connectionType, setConnectionType] = useState<
    'account' | 'organization'
  >('account');

  const roleName =
    connectionType === 'organization' ? orgRoleName : accountRoleName;
  const setRoleName =
    connectionType === 'organization' ? setOrgRoleName : setAccountRoleName;

  const validate =
    connectionType === 'organization' ? validateOrganization : validateAccount;

  const [deploymentMethod, setDeploymentMethod] = useState<AWSDeploymentMethod>(
    AWSDeploymentMethod.CloudFormation
  );

  const { user } = useUser();
  const dal = useDAL();

  const scanningAccountsList = dal.cloudAccounts.scanning.list().body;
  const scanningAccountId = scanningAccountsList?.accounts?.find(
    (x) => x.cloudProvider === Provider.Aws
  )?.providerAccountId;

  useEffect(() => {
    setLoading(validationInProgress || isCreationInProgress || hasError);
  }, [hasError, isCreationInProgress, setLoading, validationInProgress]);

  const accountId =
    connectionType === 'organization'
      ? 'AWS management account ID'
      : 'AWS account ID';

  const createdText =
    connectionType === 'organization'
      ? 'Organization connected'
      : 'Account created';

  const doneOnClick = () => {
    if (connectionType === 'organization') {
      void connectOrganization();
    } else if (initialAccount) {
      void updateAccount();
    } else {
      void createAccount();
    }
  };

  return (
    <Box className='px-[20px]'>
      <Box className='my-[15px] mb-[30px]'>
        <Stack direction='column' gap='16px'>
          <Typography marginBottom='8px' variant='body2'>
            Deployment method
          </Typography>
          <Stack
            className={'cursor-pointer'}
            direction='row'
            alignItems='center'
            gap='12px'
            onClick={() => {
              setDeploymentMethod(AWSDeploymentMethod.CloudFormation);
              setHasError(false);
            }}
          >
            <Radio
              data-testid='full-instance-radio'
              disabled={false}
              size='small'
              checked={deploymentMethod === AWSDeploymentMethod.CloudFormation}
            />
            <Typography variant='body2'>CloudFormation</Typography>
          </Stack>
          <Stack
            className={'cursor-pointer'}
            direction='row'
            alignItems='center'
            gap='12px'
            onClick={() => {
              setDeploymentMethod(AWSDeploymentMethod.Terraform);
              setConnectionType('account');
              setHasError(false);
            }}
          >
            <Radio
              disabled={false}
              data-testid='specific-vol-radio'
              size='small'
              checked={deploymentMethod === AWSDeploymentMethod.Terraform}
            />
            <Typography variant='body2'>Terraform</Typography>
          </Stack>
        </Stack>
      </Box>
      <>
        {accountType === CloudAccountType.Source &&
          !initialAccount &&
          deploymentMethod === AWSDeploymentMethod.CloudFormation && (
            <Stack direction='column' gap='16px'>
              <Typography marginBottom='8px' variant='body2'>
                What do you want to connect?
              </Typography>
              <Stack
                className='cursor-pointer'
                direction='row'
                alignItems='center'
                gap='12px'
                onClick={() => setConnectionType('account')}
              >
                <Radio
                  data-testid='full-instance-radio'
                  size='small'
                  checked={connectionType === 'account'}
                />
                <Typography variant='body2'>Account</Typography>
              </Stack>
              <Stack
                className='cursor-pointer'
                direction='row'
                alignItems='center'
                gap='12px'
                onClick={() => setConnectionType('organization')}
              >
                <Radio
                  data-testid='specific-vol-radio'
                  size='small'
                  checked={connectionType === 'organization'}
                />
                <Typography variant='body2'>Organization</Typography>
              </Stack>
            </Stack>
          )}
        {connectionType === 'account' && (
          <Box className='my-[15px]'>
            Before continuing, make sure you have permissions to create roles.
          </Box>
        )}
      </>
      <Box className='flex flex-col justify-center my-[30px] gap-[8px]'>
        <FormControl size='small' className='mb-[12px]'>
          <FormLabel>{accountId}</FormLabel>
          <TextField
            size='medium'
            inputProps={{
              sx: { height: '36px' },
              maxLength: 12,
              'data-testid': 'account-id',
            }}
            disabled={
              deploymentMethod !== AWSDeploymentMethod.Terraform &&
              (validationInProgress ||
                isCreationInProgress ||
                hasError ||
                !!initialAccount)
            }
            value={awsAccountId}
            onChange={(event) => setAccountId(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void validate()}
          />
        </FormControl>
        {connectionType === 'organization' && (
          <FormControl size='small' className='mb-[12px]'>
            <FormLabel>Organization OU ID</FormLabel>
            <TextField
              size='medium'
              inputProps={{ style: { height: '36px' } }}
              value={organizationOUID}
              disabled={
                validationInProgress || isCreationInProgress || hasError
              }
              onChange={(event) => {
                setOrganizationOUID(event.target.value);
              }}
              onKeyDown={(event) => event.key === 'Enter' && void validate()}
            />
          </FormControl>
        )}
        <FormControl size='small' className='mb-[12px]'>
          <FormLabel>Role name</FormLabel>
          <TextField
            size='medium'
            inputProps={{ style: { height: '36px' } }}
            value={roleName}
            disabled={
              deploymentMethod !== AWSDeploymentMethod.Terraform &&
              (validationInProgress || isCreationInProgress || hasError)
            }
            onChange={(event) => setRoleName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void validate()}
          />
        </FormControl>
        {deploymentMethod === AWSDeploymentMethod.CloudFormation ? (
          <>
            {!validationInProgress &&
              !isCreationInProgress &&
              !isCreated &&
              !hasError && (
                <Button
                  variant='contained'
                  className='px-[20px] mt-[20px] w-auto'
                  sx={{ height: 48 }}
                  data-testid='open-aws-console'
                  disabled={
                    !awsAccountId ||
                    !roleName ||
                    (connectionType === 'organization' && !organizationOUID)
                  }
                  onClick={() => void validate()}
                >
                  Open AWS Console{' '}
                  <i className='material-symbols-open-in-new w-[20px] h-[20px] ml-[8px]' />
                </Button>
              )}
            {(validationInProgress || isCreationInProgress) && (
              <Box
                className='flex items-center gap-[8px] ml-[20px] justify-between'
                sx={{ color: 'var(--mui-palette-background-lightGray)' }}
              >
                <Stack alignItems='center' direction='row' gap='12px'>
                  <Loader sx={{ width: 20 }} />
                  {validationInProgress && <>Waiting for permissions...</>}
                  {isCreationInProgress && <>Trying to connect...</>}
                </Stack>
                {!isCreationInProgress && (
                  <>
                    <Button
                      variant='contained'
                      data-testid='aws-console-done'
                      onClick={doneOnClick}
                    >
                      I&apos;m Done
                    </Button>
                  </>
                )}
              </Box>
            )}
            {isCreated && (
              <Box
                className='flex items-center gap-[4px] ml-[20px] h-[48px]'
                sx={{ color: 'var(--mui-palette-success-main)' }}
              >
                <i className='material-symbols-check w-[20px] h-[20px]' />{' '}
                {createdText}
              </Box>
            )}
            {hasError && (
              <Box
                className='flex items-center gap-[4px] ml-[20px] h-[48px]'
                sx={{ color: 'var(--mui-palette-error-main)' }}
              >
                <i className='material-symbols-close-rounded w-[20px] h-[20px]' />{' '}
                Something went wrong
                <Divider
                  orientation='vertical'
                  flexItem
                  sx={{
                    borderColor: 'var(--mui-palette-background-lightGray)',
                  }}
                  className='mx-[6px] my-auto h-[20px]'
                />
                <Button
                  variant='contained'
                  onClick={initialAccount ? () => void updateAccount() : retry}
                >
                  Try Again
                </Button>
              </Box>
            )}
          </>
        ) : null}
      </Box>
      <Box>
        {deploymentMethod === AWSDeploymentMethod.CloudFormation && (
          <Box className='my-[15px]'>
            We&apos;ll take you straight to the AWS console to set Eon&apos;s
            permissions. Everything is ready for you, just jump between the
            steps, confirm, and get back to us.
          </Box>
        )}
        {deploymentMethod === AWSDeploymentMethod.Terraform && (
          <>
            <Typography
              variant='body2'
              className='mt-[28px] mb-[8px] font-bold'
              color='text.secondary'
            >
              Account deployment
            </Typography>

            <ol className='list-decimal mx-[20px]'>
              <li className='mb-[8px]'>
                <Typography
                  variant='body2'
                  className='mb-[8px]'
                  color='text.secondary'
                >
                  <b>Add the Eon Terraform module to your deployment file</b>
                  <br />
                  Add this snippet to your Terraform deployment file and apply.
                </Typography>
              </li>
            </ol>
            <Box
              className='flex flex-col items-center justify-center my-[30px]'
              gap='8px'
            >
              <CopyableCode
                code={
                  accountType === CloudAccountType.Source
                    ? `module "eon_source_account_infra" {
    source = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-aws-source-account-tf.zip"
    eon_account_id = "${user?.eonAccount.id}"
    scanning_account_id = "${scanningAccountId}"
    role_name = "${roleName}"
  }`
                    : `module "eon_restore_account_infra" {
    source = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-aws-restore-account-tf.zip"
    eon_account_id = "${user?.eonAccount.id}" 
    role_name = "${roleName}"
  }`
                } // SurDin: why can user be undefined?
              />
            </Box>
            <ol start={2} className='list-decimal mx-[20px]'>
              <li>
                <Typography
                  variant='body2'
                  className='mb-[8px]'
                  color='text.secondary'
                >
                  <b>Confirm the deployment</b>
                  <br />
                  After the Terraform deployment is done, click the button below
                  to validate and connect the account to your Eon account.
                </Typography>
              </li>
            </ol>

            <Box
              className='flex flex-col items-center justify-center mb-[8px]'
              gap='8px'
            >
              <Button
                variant='contained'
                disabled={false}
                data-testid='azure-connect-complete-btn'
                onClick={doneOnClick}
                sx={{ width: '400px', mt: '24px' }}
              >
                Connect account
              </Button>
            </Box>
            {hasError && (
              <Typography
                variant='body1'
                color='error'
                data-testid='error-message'
              >
                <Stack
                  className='my-[15px]'
                  direction='row'
                  alignItems='center'
                >
                  <i className='material-symbols-error-outline-rounded mr-[8px] text-[24px]'></i>
                  Error encountered
                </Stack>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
